import { useEffect, useState, Fragment } from "react";
import api from "@/lib/api";
import { Loader2, AlertTriangle, RefreshCw, Receipt } from "lucide-react";

const money = (v) => `£${(v ?? 0).toFixed(2)}`;

export default function AdminReconciliationTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);

  const load = async () => {
    setBusy(true);
    try { const r = await api.get("/admin/reconciliation", { params: { limit: 200 } }); setData(r.data); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-accent" />;
  const t = data.totals;

  return (
    <div className="space-y-5" data-testid="admin-reconciliation">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-semibold flex items-center gap-2"><Receipt className="h-5 w-5 text-accent" /> Stripe fee reconciliation</h3>
          <p className="text-sm text-muted-foreground mt-0.5">{data.note}</p>
        </div>
        <button onClick={load} disabled={busy} className="px-4 py-2 rounded-full border border-border text-sm font-medium inline-flex items-center gap-1.5 hover:bg-secondary transition-colors disabled:opacity-50" data-testid="recon-refresh">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh from Stripe
        </button>
      </div>

      <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="recon-totals">
        {[["Customer paid", t.customer_paid], ["Stripe fees", t.stripe_fees], ["Cleaner amount", t.cleaner_amount],
          ["Service fees kept", t.service_fees], ["Admin Fees collected", t.admin_fees], ["Net position", t.net_position]].map(([label, v]) => (
          <div key={label} className="bg-card rounded-2xl border border-primary/5 p-4">
            <p className="font-display text-lg font-semibold">{money(v)}</p>
            <p className="text-xs text-muted-foreground mt-1">{label}</p>
          </div>
        ))}
      </div>

      {data.discrepancy_count > 0 && (
        <div className="rounded-2xl border-2 border-red-200 bg-red-50 p-4 text-sm text-red-900 flex gap-2" data-testid="recon-discrepancies">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <p><strong>{data.discrepancy_count} booking(s)</strong> where Stripe's actual cost exceeded the Admin Fee collected. Open the row for the full breakdown.</p>
        </div>
      )}

      <div className="bg-card rounded-2xl border border-primary/5 overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-secondary/50 text-left">
            <tr>
              {["Booking", "Date", "Customer → Cleaner", "Service", "£1.99 fee", "Admin Fee", "Customer total", "Stripe fee", "Net kept", "Status"].map((h) => (
                <th key={h} className="px-3 py-2.5 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <Fragment key={`${r.booking_id}-${i}`}>
                <tr key={r.booking_id} onClick={() => setOpen(open === r.booking_id ? null : r.booking_id)}
                  className={`border-t border-border cursor-pointer hover:bg-secondary/30 ${r.discrepancy ? "bg-red-50/60" : ""}`}
                  data-testid={`recon-row-${r.booking_id}`}>
                  <td className="px-3 py-2.5 font-mono text-xs">{r.booking_id.slice(-8)}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{r.date}</td>
                  <td className="px-3 py-2.5">{r.customer} → {r.cleaner}</td>
                  <td className="px-3 py-2.5">{money(r.service_price)}</td>
                  <td className="px-3 py-2.5">{money(r.mcc_service_fee)}</td>
                  <td className="px-3 py-2.5">{money(r.admin_fee_charged)}</td>
                  <td className="px-3 py-2.5 font-medium">{money(r.customer_total)}</td>
                  <td className="px-3 py-2.5">
                    {money(r.stripe_fee)}
                    <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${r.stripe_fee_is_actual ? "bg-emerald-100 text-emerald-800" : "bg-secondary text-muted-foreground"}`}>
                      {r.stripe_fee_is_actual ? "actual" : "est"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">{money(r.mcc_net_retained)}</td>
                  <td className="px-3 py-2.5 text-xs capitalize">{r.payment_status} / {r.payout_status?.replace(/_/g, " ")}</td>
                </tr>
                {open === r.booking_id && (
                  <tr key={`${r.booking_id}-d`} className="border-t border-border bg-secondary/20">
                    <td colSpan={10} className="px-4 py-4" data-testid={`recon-detail-${r.booking_id}`}>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1 text-sm">
                        <Item k="Customer payment" v={money(r.customer_total)} />
                        <Item k="− Stripe fee" v={`${money(r.stripe_fee)} (${r.stripe_fee_source})`} />
                        <Item k="− Cleaner amount" v={money(r.cleaner_amount)} />
                        <Item k="Commission (15%)" v={money(r.commission)} />
                        <Item k="£1.99 service revenue" v={money(r.mcc_service_fee)} />
                        <Item k="Priority fee" v={money(r.priority_fee)} />
                        <Item k="Admin Fee recovery" v={money(r.admin_fee_recovery)} />
                        <Item k="Rounding difference" v={money(r.rounding_difference)} />
                        <Item k="Net position" v={money(r.mcc_net_retained)} />
                        <Item k="Card profile" v={r.card_profile || "—"} />
                        <Item k="Booking status" v={r.booking_status} />
                        <Item k="Refund status" v={r.refund_status} />
                        <Item k="Payout status" v={r.payout_status} />
                        <Item k="Payment intent" v={r.stripe_payment_intent_id || "—"} />
                        <Item k="Charge" v={r.stripe_charge_id || "—"} />
                        {r.legacy_pre_admin_fee && <Item k="Note" v="Booked before the Admin Fee existed" />}
                      </div>
                      {r.discrepancy && (
                        <p className="mt-3 text-sm text-red-800 font-medium">
                          Stripe charged more than the Admin Fee collected on this booking — check the card profile used.
                        </p>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        {data.rows.length === 0 && <p className="text-muted-foreground py-10 text-center">No bookings yet.</p>}
      </div>
    </div>
  );
}

function Item({ k, v }) {
  return <p><span className="text-muted-foreground">{k}: </span><span className="font-medium break-all">{v}</span></p>;
}
